import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from 'ngx-toastr';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { GetQuizByAttemptIdResponse } from '@edtech-ui/core/models/interfaces/attempt-Quiz/quiz-Question';
import { Subscription } from 'rxjs';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';

@Component({
	selector: 'app-quiz-result-dialog',
	standalone: true,
	imports: [CommonModule, MatButtonModule, MatIconModule, MatDialogModule, HasPermissionDirective],
	templateUrl: './quiz-result-dialog.component.html',
	styleUrls: ['./quiz-result-dialog.component.scss']
})
export class QuizResultDialogComponent implements OnInit, OnDestroy {
	finishedTime: string;
	attemptId: string;
	resultData: any;
	classroomId: string | null = null;
	isLoading: boolean = false;
	subscription = new Subscription();
	PermissionForStudents = PermissionForStudents;
	hasQuizResultPermission: boolean = false;

	constructor(
		public dialogRef: MatDialogRef<QuizResultDialogComponent>,
		private router: Router,
		private toastr: ToastrService,
		private quizService: QuizManagementService,
		private route: ActivatedRoute,
		private _authService: AuthServiceService,
		@Inject(MAT_DIALOG_DATA) public data: { finishedTime: string; attemptId: string; result: any, classId: number, courseId: string }
	) {
		this.finishedTime = data.finishedTime || '00:00:00';
		this.attemptId = data.attemptId;
		this.resultData = data.result || {};
	}

	ngOnInit() {
		if (this.attemptId && this.hasQuizResultPermission) {
			this.fetchQuizDetails();
		}
		this.hasQuizResultPermission = this._authService.hasPermission(PermissionForStudents.QUIZ_RESULT);
	}

	fetchQuizDetails() {
		this.isLoading = true;
		this.subscription.add(
			this.quizService.getQuizByAttemptId(this.attemptId).subscribe({
				next: (response: GetQuizByAttemptIdResponse) => {
					if (response.Success && response.Data) {
						this.classroomId = response.Data.classroom_id.toString();
						this.resultData = response.Data;
					} else {
						this.toastr.error(response.Message || 'Failed to fetch quiz details.', 'Error', { timeOut: 3000 });
					}
					this.isLoading = false;
				},
				error: (error) => {
					this.toastr.error('Failed to fetch quiz details.', 'Error', { timeOut: 3000 });
					this.isLoading = false;
				}
			}));
	}

	get score_percentage() {
		if (this.resultData?.quiz_conversation) {
			const total_marks = this.resultData.quiz_conversation.reduce((sum: number, q: any) => sum + (q.questions.marks || 0), 0);
			const obtainedMarks = this.resultData.quiz_conversation.reduce((sum: number, q: any) => sum + (q.evaluation.obtained_marks || 0), 0);
			return total_marks ? Math.round((obtainedMarks / total_marks) * 100) : 0;
		}
		return this.resultData?.overall_user_performance?.score_percentage?.replace('%', '') || 0;
	}

	get totalQuestions() {
		return this.resultData?.quiz_conversation?.length || this.resultData?.overall_user_performance?.total_number_of_question || 0;
	}

	get correctAnswers() {
		return this.resultData?.quiz_conversation?.filter((q: any) => q.evaluation.answer_type === 'correct').length ||
			this.resultData?.overall_user_performance?.total_number_of_correct_answer || 0;
	}

	get wrongAnswers() {
		return this.resultData?.quiz_conversation?.filter((q: any) => q.evaluation.answer_type == 'incorrect').length ||
			this.resultData?.overall_user_performance?.total_number_of_incorrect_answer || 0;
	}

	get skippedAnswers() {
		return this.resultData?.overall_user_performance?.total_number_of_skipped_answer || 0;
	}

	get partialCorrectAnswers() {
		return this.resultData?.overall_user_performance?.total_number_of_partial_correct_answer || 0;
	}

	get gainedPoints() {
		return this.resultData?.overall_user_performance?.gained_points || 0;
	}

	backToQuizPage() {
		this.dialogRef.close();
		const { classId, courseId } = this.data;
		this.router.navigate(['/student-dashboard/course-detail-page', classId, courseId, 'quiz-list']);
	}

	viewDetailedResult() {
		const { attemptId, classId, courseId } = this.data;
		if (!attemptId || !classId || !courseId) {
			this.toastr.error('Missing navigation data');
			return;
		}
		this.router.navigate(['/student-dashboard', 'course-detail-page', classId, courseId, 'detailed-solutions', attemptId]);
		this.dialogRef.close();
	}

	get timeTaken(): string {
		if (!this.resultData?.time_taken) return '00:00:00';
		const totalSeconds = this.resultData.time_taken;
		const hours = Math.floor(totalSeconds / 3600);
		const minutes = Math.floor((totalSeconds % 3600) / 60);
		const seconds = totalSeconds % 60;
		return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}